import styled, { css } from 'styled-components';
import chroma from 'chroma-js';
import { Span, P, H5, UnstyledButton, IconButton } from '@workramp/ui';

interface LinkProps {
  $isActive?: boolean;
}

interface ExpandedProps {
  $isExpanded?: boolean;
}

const linkMixin = css<LinkProps>`
  position: relative;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.on[800]};
  height: ${({ theme }) => theme.spacing(10)}px;
  padding-left: ${({ theme }) => theme.spacing(6)}px;
  padding-right: ${({ theme }) => theme.spacing(6)}px;
  overflow: hidden;
  text-decoration: none;

  &:link,
  &:visited {
    color: ${({ theme }) => theme.colors.on[800]};
  }

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: none;
    outline: none;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: -2px;
        height: 100%;
        width: 4px;
        border-radius: 4px;
        background-color: ${({ theme }) => theme.colors.on[800]};
      }
    `}
`;

export const Wrapper = styled.div`
  height: 100%;
  overflow: auto;
  padding-top: ${({ theme }) => theme.spacing(4)}px;
  padding-bottom: ${({ theme }) => theme.spacing(4)}px;
  box-sizing: border-box;
`;

export const SidebarHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.surface[0]};
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: ${({ theme }) => theme.spacing(2)}px;
    background: linear-gradient(to bottom, ${({ theme }) => theme.colors.surface[0]}, transparent);
  }
`;

export const SidebarBody = styled.div`
  padding-left: ${({ theme }) => theme.spacing(6)}px;
  padding-right: ${({ theme }) => theme.spacing(6)}px;
`;

export const Logo = styled.img`
  height: ${({ theme }) => theme.spacing(8)}px;
  max-width: 100%;
  object-fit: contain;
`;

export const MenuItemWrapper = styled.div`
  position: relative;
`;

export const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const MenuLink = styled.a<LinkProps>`
  ${linkMixin}
`;

export const MenuText = styled(Span)`
  margin-left: ${({ theme }) => theme.spacing(6)}px;
`;

export const MenuSubtext = styled(Span)`
  margin-left: ${({ theme }) => theme.spacing(12)}px;
`;

export const ExpansionButton = styled(IconButton)<ExpandedProps>`
  position: absolute;
  top: 0;
  right: 0;
  height: ${({ theme }) => theme.spacing(10)}px;
  width: ${({ theme }) => theme.spacing(10)}px;
  line-height: ${({ theme }) => theme.spacing(10)}px;
  transition: transform ${({ theme }) => theme.motion.duration.short}ms ease-in-out;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};

  &:hover {
    color: ${({ theme }) => theme.colors.primary[600]};
  }
`;

export const ProfileWrapper = styled.div`
  text-align: center;
`;

export const ProfileSecondaryText = styled(P)`
  color: ${({ theme }) => theme.colors.on[500]};
`;

export const ProfilePictureWrapper = styled(UnstyledButton)`
  display: inline-block;
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;
  padding: unset;
  background-color: transparent;
  border: none;
  border-radius: 100%;
`;

export const ViewPictureOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.on[0]};
  background-color: ${({ theme }) =>
    theme.colors.surface[900] && chroma(theme.colors.surface[900]).alpha(0.8).hex('rgba')};
  border-radius: 100%;
  cursor: pointer;
  visibility: hidden;
  opacity: 0;
  transition:
    visibility ${({ theme }) => theme.motion.duration.short}ms ease-in-out,
    opacity ${({ theme }) => theme.motion.duration.short}ms ease-in-out;

  ${ProfilePictureWrapper}:hover &, ${ProfilePictureWrapper}:focus & {
    visibility: visible;
    opacity: 1;
  }
`;

export const ProfileNameLink = styled.a`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;

  &:link,
  &:visited {
    color: ${({ theme }) => theme.colors.on[900]};
  }

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: none;
  }
`;

// The built in truncation isn't used here as it uses -webkit-line-clamp under the hood.
// This attribute has issues when used inside of links and will sometimes truncate in the
// middle of text instead of the end.
// https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp.
export const ProfileName = styled(H5)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
